import React from 'react';
import { useMemoryUsage, useNetworkPerformance } from '../../shared/lib/hooks/use-performance';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Badge } from './badge';

interface PerformanceMonitorProps {
  show?: boolean;
}

export const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({ 
  show = process.env.NODE_ENV === 'development' 
}) => {
  const memoryInfo = useMemoryUsage();
  const networkInfo = useNetworkPerformance();

  if (!show) return null;

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getNetworkColor = (effectiveType: string) => {
    switch (effectiveType) {
      case 'slow-2g':
      case '2g':
        return 'destructive';
      case '3g':
        return 'secondary';
      case '4g':
        return 'default';
      default:
        return 'default';
    }
  };

  const getMemoryColor = (usedPercent: number) => {
    if (usedPercent > 80) return 'destructive';
    if (usedPercent > 60) return 'secondary';
    return 'default';
  };

  const memoryUsedPercent = memoryInfo.totalJSHeapSize > 0 
    ? (memoryInfo.usedJSHeapSize / memoryInfo.totalJSHeapSize) * 100 
    : 0;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-xs">
      <Card className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Performance Monitor</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          {/* Memory Usage */}
          <div className="flex items-center justify-between">
            <span>Memory:</span>
            <Badge variant={getMemoryColor(memoryUsedPercent)}>
              {formatBytes(memoryInfo.usedJSHeapSize)} / {formatBytes(memoryInfo.totalJSHeapSize)}
            </Badge>
          </div>
          
          {/* Network */}
          <div className="flex items-center justify-between">
            <span>Network:</span>
            <Badge variant={getNetworkColor(networkInfo.effectiveType)}>
              {networkInfo.effectiveType.toUpperCase()}
            </Badge>
          </div>
          
          {/* Connection Speed */}
          <div className="flex items-center justify-between">
            <span>Downlink:</span>
            <span>{networkInfo.downlink} Mbps</span>
          </div>
          
          {/* RTT */}
          <div className="flex items-center justify-between">
            <span>RTT:</span>
            <span>{networkInfo.rtt}ms</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};